<template>
    <div class="artist-info-container">
        <p class="title">Let's set up your Artist Profile</p>
        <div class="fields">
            <div class="name">
                <label for="band_name">Artist Name *</label>
                <input
                    v-model="$v.userData.artistInfo.band_name.$model"
                    type="text"
                    placeholder="The name you perform under"
                />
                <p
                    v-if="
                        $v.userData.artistInfo.band_name.$dirty &&
                        !$v.userData.artistInfo.band_name.required
                    "
                    class="error-msg"
                >
                    Please enter a band name.
                </p>
                <p
                    v-if="
                        $v.userData.artistInfo.band_name.$dirty &&
                        !$v.userData.artistInfo.band_name.maxLength
                    "
                    class="error-msg"
                >
                    Please enter a shorter band name.
                </p>
            </div>
            <div class="location">
                <div class="address">
                    <label for="home_location">Where are you based? *</label>
                    <input
                        ref="autocomplete"
                        v-model="$v.userData.artistInfo.home_location.$model"
                        type="text"
                        placeholder="City"
                    />
                    <input type="hidden" v-model="$v.userData.artistInfo.lat.$model" />
                    <input type="hidden" v-model="$v.userData.artistInfo.lon.$model" />
                    <p v-if="locationError" class="error-msg">Please select a location.</p>
                </div>
                <div class="radius">
                    <label for="travel_radius">Travel Radius</label>
                    <input
                        v-model="$v.userData.artistInfo.travel_radius.$model"
                        type="number"
                        placeholder="Miles"
                    />
                    <p
                        v-if="
                            $v.userData.artistInfo.travel_radius.$dirty &&
                            !$v.userData.artistInfo.travel_radius.minValue
                        "
                        class="error-msg"
                    >
                        Please enter at least 1 mile.
                    </p>
                </div>
            </div>
            <div class="vibes">
                <label for="keyword">How would you describe your music? *</label>
                <input
                    v-model="$v.userData.artistInfo.keyword.$model"
                    type="text"
                    placeholder="Add keywords that describe your sound"
                />
                <p
                    v-if="
                        $v.userData.artistInfo.keyword.$dirty &&
                        !$v.userData.artistInfo.keyword.required
                    "
                    class="error-msg"
                >
                    Please enter at least 1 vibe.
                </p>
                <p
                    v-if="
                        $v.userData.artistInfo.keyword.$dirty &&
                        !$v.userData.artistInfo.keyword.correctFormat
                    "
                    class="error-msg"
                >
                    Please enter comma separated keywords.
                </p>
                <p class="instruction">Separated by a comma</p>
            </div>
        </div>
    </div>
</template>
<script>
    import { required, minValue, maxLength } from 'vuelidate/lib/validators'

    export default {
        name: 'ArtistInfo',
        props: {
            userData: {
                type: Object,
                required: true
            },
            validation: {
                type: Object,
                required: false
            }
        },
        validations: {
            userData: {
                artistInfo: {
                    band_name: { required, maxLength: maxLength(50) },
                    home_location: { required },
                    lat: { required },
                    lon: { required },
                    travel_radius: { minValue: minValue(1) },
                    keyword: {
                        required,
                        correctFormat: function (value) {
                            return /^[&0-9a-zA-Z+\-_ ]+(,[&0-9a-zA-Z+\-_ ]+)*$/.test(value)
                        }
                    }
                }
            }
        },
        computed: {
            locationError() {
                return (
                    (this.$v.userData.artistInfo.home_location.$dirty &&
                        !this.$v.userData.artistInfo.home_location.required) ||
                    (this.$v.userData.artistInfo.lat.$dirty &&
                        !this.$v.userData.artistInfo.lat.required) ||
                    (this.$v.userData.artistInfo.lon.$dirty &&
                        !this.$v.userData.artistInfo.lon.required)
                )
            }
        },
        watch: {
            $v: {
                handler: function (validation) {
                    this.$emit('update:validation', validation)
                },
                immediate: true
            },
            '$v.userData.artistInfo.home_location.$model': function (location) {
                if (location) {
                    this.$v.userData.artistInfo.lat.$touch()
                    this.$v.userData.artistInfo.lon.$touch()
                }
            }
        },
        mounted() {
            this.$nextTick(() => {
                if (this.$refs?.autocomplete) {
                    this.$emit('autocomplete-loaded', this.$refs.autocomplete)
                }
            })
        }
    }
</script>
<style lang="scss" scoped>
    @import '../../../../sass/_variables.scss';
    .artist-info-container {
        p {
            margin-bottom: 0px;
        }
        button {
            border: none;
        }

        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 40px;

        .title {
            color: #dedede;
            @include base-font-24;
            font-weight: 600;
            line-height: 28px;
        }
        .fields {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;

            .error-msg {
                color: #ed3c0d;
                font-family: Instrument Sans;
                font-size: 8px;
                font-style: normal;
                font-weight: 600;
                line-height: 130%;
                letter-spacing: 0.14px;
                text-wrap: nowrap;
            }
            label,
            .instruction {
                padding: 4px 2px;
                color: #acacac;
                font-family: Instrument Sans;
                font-size: 12px;
                font-style: normal;
                font-weight: 650;
                line-height: 135%;
                letter-spacing: 0.12px;
            }
            input {
                padding: 12px 16px 16px 16px;
                border-radius: 12px;
                border: 1px solid #8b8b8b;
                background: #262626;
                overflow: hidden;
                color: #dedede;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-family: Instrument Sans;
                font-size: 16px;
                font-style: normal;
                font-weight: 550;
                line-height: 125%;
                align-self: stretch;
                width: 100%;
                -moz-appearance: textfield;
                appearance: textfield;
            }
            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }
            input::placeholder {
                overflow: hidden;
                color: #646464;
                text-overflow: ellipsis;
                @include base-font-16;
                line-height: 125%;
            }
            .name {
                width: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: flex-start;
            }
            .location {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-items: center;
                gap: 16px;

                .address,
                .radius {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    justify-content: center;
                }
            }
            .vibes {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: center;
            }
        }
    }
</style>
